'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { Camera, Trash2, Loader2, Package, Eye, ScanLine } from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';
import { toast } from 'sonner';
import { Link } from '@/i18n/routing';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SelectAllBanner } from '@/components/select-all-banner';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { PermissionGate } from '@/features/auth';
import { SORTING_HASH } from '../constants';
import { sortingQueries } from '../services/sorting.queries';
import { useBulkDeleteSortingMutation } from '../services/sorting.mutations';
import { SortingTableActionBar } from './sorting-table-action-bar';
import { SortingCard } from './sorting-card';
import { SortingCompactRow } from './sorting-compact-row';
import type {
  SortingSession,
  SortingListParams,
  SortingSessionStatus
} from '../types';

const STATUS_STYLES: Record<SortingSessionStatus, string> = {
  pending:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  in_progress:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

export function SortingListing() {
  const t = useTranslations('sorting');
  const tc = useTranslations('common');
  const router = useRouter();
  // Overlay open-state lives in the URL hash (#delete/<id>, #scan) so overlays
  // are deep-linkable and close on browser Back. Destructure the stable
  // callbacks so they can go in hook dep arrays without tripping the React
  // Compiler's manual-memoization check.
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(SORTING_HASH.delete);
  const [scannerActive, setScannerActive] = useState(true);
  const { open: scanDialogOpen, onOpenChange: toggleScanDialog } =
    useHashDialog(SORTING_HASH.scan);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );
  const [view, setView] = useDataView('sorting');

  const { data: hwScanData, isFetching: hwScanFetching } = useQuery({
    ...sortingQueries.list({ search: hwScan?.value ?? '', per_page: 1 }),
    enabled: !!hwScan
  });

  const statusLabels: Record<SortingSessionStatus, string> = {
    pending: t('statusPending'),
    in_progress: t('statusInProgress'),
    completed: t('statusCompleted')
  };

  const columns: ColumnDef<SortingSession>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'box_number',
      accessorFn: (row) => row.box?.box_number ?? '-',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('boxNumber')} />
      ),
      cell: ({ row, column }) => {
        const value = row.original.box?.box_number;
        return (
          <Link
            href={`/dashboard/sorting/${row.original.id}`}
            className='text-primary font-medium hover:underline'
          >
            {value ? (
              <HighlightMatch
                text={value}
                query={getColumnSearchTerms(column)}
              />
            ) : (
              '-'
            )}
          </Link>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'sorting.column.boxNumber'
      }
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('sortingDate')} />
      ),
      cell: ({ row }) => {
        const date = row.original.created_at;
        return date
          ? new Date(date).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          : '-';
      },
      enableColumnFilter: true,
      meta: {
        label: t('sortingDate'),
        variant: 'dateRange' as const,
        permission: 'sorting.column.sortingDate'
      }
    },
    // {
    //   id: 'box_type_id',
    //   accessorFn: (row) => row.box?.id ?? null,
    //   header: () => null,
    //   cell: () => null,
    //   enableSorting: false,
    //   enableHiding: true,
    //   enableColumnFilter: true,
    //   meta: {
    //     label: t('boxNumber'),
    //     variant: 'multiSelect' as const,
    //     options: ((boxTypesData?.data ?? []) as BoxType[]).map((bt) => ({
    //       label: bt.name,
    //       value: String(bt.id)
    //     }))
    //   }
    // },
    {
      id: 'total_containers',
      accessorKey: 'total_containers',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('totalFiles')} />
      ),
      cell: ({ row }) => (row.original.total_containers ?? 0).toLocaleString(),
      meta: {
        label: t('totalFiles'),
        permission: 'sorting.column.totalFiles'
      }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant='secondary' className={cn(STATUS_STYLES[status])}>
            {statusLabels[status] ?? status}
          </Badge>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterStatus'),
        variant: 'multiSelect' as const,
        icon: 'solar:tag-bold-duotone',
        options: [
          { label: t('statusPending'), value: 'pending' },
          { label: t('statusInProgress'), value: 'in_progress' },
          { label: t('statusCompleted'), value: 'completed' }
        ],
        permission: 'sorting.column.status'
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center justify-end gap-1'>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => router.push(`/dashboard/sorting/${item.id}`)}
            >
              <Eye className='h-4 w-4' />
            </Button>
            <PermissionGate permissions={['sorting.delete']}>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => openDelete(String(item.id))}
              >
                <Trash2 className='text-destructive h-4 w-4' />
              </Button>
            </PermissionGate>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'box_number',
    parseAsString.withDefault('')
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedAt] = useQueryState(
    'created_at',
    parseAsString.withDefault('')
  );
  const [urlBoxTypeId] = useQueryState(
    'box_type_id',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<SortingSession>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<SortingSession>[])
  );

  const queryParams = useMemo<SortingListParams>(() => {
    const params: SortingListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) params.search = urlSearch;
    if (urlStatus.length) params.status = urlStatus;

    if (urlCreatedAt) {
      const toDateStr = (ts: string) => {
        const d = new Date(Number(ts));
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };
      const parts = String(urlCreatedAt).split(',').filter(Boolean);
      if (parts[0]) params.date_from = toDateStr(parts[0]);
      if (parts[1]) params.date_to = toDateStr(parts[1]);
    }

    if (urlBoxTypeId.length) {
      params.box_type_id = urlBoxTypeId.map(Number);
    }

    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlStatus,
    urlCreatedAt,
    urlBoxTypeId,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    sortingQueries.list(queryParams)
  );
  const deleteMutation = useBulkDeleteSortingMutation();

  const sessions = (data?.data ?? []) as SortingSession[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const deleteTarget = deleteOpen
    ? (sessions.find((s) => String(s.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<SortingSession>({
    columns: filteredColumns,
    pageKey: 'sorting',
    data: sessions,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 },
      columnVisibility: { box_type_id: false }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('boxNumber'), value: 'box_number' },
      { label: t('sortingDate'), value: 'created_at' },
      { label: t('totalFiles'), value: 'total_containers' },
      { label: t('status'), value: 'status' }
    ],
    [t]
  );

  const handleView = useCallback(
    (session: SortingSession) => {
      router.push(`/dashboard/sorting/${session.id}`);
    },
    [router]
  );

  const handleDeleteTarget = useCallback(
    (session: SortingSession) => {
      openDelete(String(session.id));
    },
    [openDelete]
  );

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ ids: [deleteTarget.id] });
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    const results = (hwScanData?.data ?? []) as SortingSession[];
    if (results.length > 0) {
      router.push(`/dashboard/sorting/${results[0].id}`);
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanData, hwScanFetching, hwScan, router, t]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive && !scanDialogOpen
  });

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='sorting'
          />
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                variant='outline'
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <span className='relative me-2 flex h-2 w-2 shrink-0'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
                <ScanLine className='me-2 h-4 w-4' />
                {t('scanQR')}
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => toggleScanDialog(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='sorting.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : sessions.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Package className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noSessions')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {sessions.map((session) => (
              <SortingCard
                key={session.id}
                session={session}
                onView={handleView}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {sessions.map((session) => (
              <SortingCompactRow
                key={session.id}
                session={session}
                onView={handleView}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={sessions.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <SortingTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.box?.box_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={toggleScanDialog}
        stage='sorting'
        onScanValue={(value) => setHwScan({ value, key: Date.now() })}
        translationNamespace='sorting'
      />
    </div>
  );
}
